import React from 'react'
import { FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Combobox,
  ComboboxItem,
  Icon,
  Input,
  Tooltip,
} from '@node-space/ui-components'
import { CustomerFeeFormData } from '../schemas/customerFeeSetupSchema'
import { CustomerFeeSetupType, CustomerFeeSetupTypes, FeeStructureTypes } from '../types'

interface CustomerGlobalFeeCardProps {
  type: CustomerFeeSetupType
  customerFeePrefix: string
  currencyOptions: ComboboxItem[]
  feeStructureOptions: ComboboxItem[]
  formValues: CustomerFeeFormData
  errors: FieldErrors<CustomerFeeFormData>
  register: UseFormRegister<CustomerFeeFormData>
  setValue: UseFormSetValue<CustomerFeeFormData>
}

export const CustomerGlobalFeeCard = ({
  type,
  customerFeePrefix,
  currencyOptions,
  feeStructureOptions,
  formValues,
  errors,
  register,
  setValue,
}: CustomerGlobalFeeCardProps) => {
  const { t } = useTranslation()

  const isPayouts = type === CustomerFeeSetupTypes.PAYOUTS
  const cardTitle = isPayouts ? t('customers.feeSetup.payouts') : t('customers.feeSetup.payins')

  const currency = `${customerFeePrefix}Currency` as keyof CustomerFeeFormData
  const feeStructure = `${customerFeePrefix}FeeStructure` as keyof CustomerFeeFormData
  const fixedFee = `${customerFeePrefix}FixedFee` as keyof CustomerFeeFormData
  const percentageFee = `${customerFeePrefix}PercentageFee` as keyof CustomerFeeFormData

  const isFixedFeeDisabled = formValues[feeStructure] === FeeStructureTypes.PERCENTAGE
  const isPercentageFeeDisabled = formValues[feeStructure] === FeeStructureTypes.FIXED

  return (
    <Box className="flex-1">
      <Box
        className="bg-surface-interactive border border-stroke-interactive rounded-lg p-4"
        flexCol
        gapY={16}
      >
        <Box width="full" justify="between" flex align="start">
          <h4 className="text-base font-medium text-foreground-prominent">{cardTitle}</h4>
          <Tooltip
            bodyContent={
              isPayouts
                ? t('customers.feeSetup.payoutsGlobalFee')
                : t('customers.feeSetup.payinsGlobalFee')
            }
            type="simple"
            position="bottom"
          >
            <Button variant="ghost" iconElement={<Icon name="Info" />} />
          </Tooltip>
        </Box>

        <Box flex gapX={8} width="full">
          <Box className="flex-1">
            <Combobox
              {...register(currency)}
              id={`${type}-currency`}
              label={t('currency')}
              selectPlaceholder={t('select')}
              items={currencyOptions}
              testId={`${type}-currency-combobox`}
              onChange={value => setValue(currency, value)}
              defaultValue={formValues[currency]}
              errorText={errors[currency]?.message}
            />
          </Box>
          <Box className="flex-1">
            <Combobox
              {...register(feeStructure)}
              id={`${type}-fee-structure`}
              label={t('customers.feeSetup.feeStructure')}
              selectPlaceholder={t('select')}
              items={feeStructureOptions}
              testId={`${type}-fee-structure-combobox`}
              onChange={value => setValue(feeStructure, value)}
              defaultValue={formValues[feeStructure]}
              errorText={errors[feeStructure]?.message}
            />
          </Box>
          <Box className="flex-1">
            <Input
              {...register(fixedFee)}
              label={t('customers.feeSetup.fixedFee')}
              disabled={isFixedFeeDisabled}
              placeholderText="0.00"
              rightSuffixText={formValues[currency] || ' '}
              value={formValues[fixedFee]}
              errorText={errors[fixedFee]?.message}
              onChange={e => setValue(fixedFee, e.target.value)}
            />
          </Box>
          <Box className="flex-1">
            <Input
              {...register(percentageFee)}
              label={t('customers.feeSetup.percentageFee')}
              disabled={isPercentageFeeDisabled}
              placeholderText="0.00"
              rightSuffixText="%"
              value={formValues[percentageFee]}
              errorText={errors[percentageFee]?.message}
              onChange={e => setValue(percentageFee, e.target.value)}
            />
          </Box>
        </Box>

        <Box width="full" flex justify="start">
          <Button variant="primary">{t('save')}</Button>
        </Box>
      </Box>
    </Box>
  )
}
